// 戦闘中の画面表示 (HUD) の初見点検 (2026-10-31)。点検の記録 = docs/research/2026-10-31-hud-first-time-audit.md。
//   配布版 (指定なし) = 作者の答え (10-31 dir=hud・p1=a 警戒 0.65・p2=b 玉が消える瞬間を強く・p3=a 体力が約1秒で満ちる・
//     p4=b 足元の熱を強く・p5=c 負け画面の一言だけ・声は使わない)。
//   DEV ?hud=old = 答える前の配布版 (警戒 0.75・玉は 0.85 秒で消える・体力は一瞬で満タン・熱は弱い・負け画面は前の一言)。
//   DEV ?hud=v2 = 答えるページで比べた声つきの版 (配布版＋稽古の金の囁き)。URL は game.ts の DEV の箱の中だけで読む。
//   作者の好み: 「コードで描いた感」(線・輪・光る輪郭だけ) は避ける → 新しい図形は足さず、声 (稽古の金の囁き) が教える。
//   ここは言葉・閾値・いつ言うかだけ。描くのは src/tut_voice_view.ts drawBattleWhisper。
//   口調は台帳 docs/ledger/story.md「いまの仕様（声）」(人懐っこい軽口・褒める・「——」は間の時だけ・「大丈夫」「よくできた」「いま」は書かない)。
import type { GameState } from './types'

let look: 'answered' | 'old' | 'v2' = 'answered'
export function setHudLook(v: string | null): void { look = v === 'v2' ? 'v2' : v === 'old' ? 'old' : 'answered' }
/** 声つきの比較版 (DEV ?hud=v2) か。 */
export function hudV2(): boolean { return look === 'v2' }
/** 答える前の配布版 (DEV ?hud=old) か。 */
export function hudOld(): boolean { return look === 'old' }

/** 自分の体幹の警戒の段。ガード2回 (67〜68%) で色が変わるよう 0.65 (p1=a)。?hud=old は 0.75。 */
export const HUD_V2_WARN_AT = 0.65
export const HUD_OLD_WARN_AT = 0.75

/** 幕の玉が消える長さ (p2=b・一度強く光ってからゆっくり消える)。?hud=old は 0.85 秒 (ただし光の寿命 0.5 秒で切れていた)。 */
export const ACT_MARK_SNUFF_SEC = 1.6
export const ACT_MARK_SNUFF_OLD_SEC = 0.85
/** 玉が消える瞬間の強い光の長さ。 */
export const ACT_MARK_FLASH_SEC = 0.32
/** 幕が変わった後、相手の体力が左から右へ満ちる秒数 (p3=a)。 */
export const ACT_REFILL_SEC = 1.0
/** 灯の消える知らせ (lamp_snuff) を残す秒数。玉の消えと体力の満ちの両方が見える長さ。 */
export function lampSnuffLifeSec(): number { return look === 'old' ? 0.5 : Math.max(ACT_MARK_SNUFF_SEC, ACT_REFILL_SEC) }

/** 声のひと言。act = 初めて幕を閉じた時の2つ (い-ウ → う-イ)・stun = 崩れた時 (お-ア)。 */
export const HUD_V2_LINES = {
  actMarks: '名前の横の玉は、相手に残った幕の数だよ。ひとつ閉じたね',
  actRefill: '幕が変わると、相手の体力はまた満ちるの。焦らなくていいよ',
  stunParry: '崩れても、紫の一撃はパリィで弾けるよ',
} as const

/** 負け画面の一言 (体幹が溜まりきって決め技で負けた時・p5=c)。render.ts deathTipFor が使う。 */
export const HUD_V2_EXEC_DEATH_TIP = '紫に光る一撃は、崩れていてもパリィで弾ける'
export const HUD_OLD_EXEC_DEATH_TIP = '体幹が溜まりきると決め技が来る。パリィなら溜まるのはわずか'

/** 崩れた時に言う回数 (最初の数回だけ)。 */
export const HUD_V2_STUN_SAY_TIMES = 3
/** 1つのひと言を出しておく秒数 (一字ずつ出し切ってから2秒ほど読める)。 */
export const HUD_V2_LINE_SEC = 4.2

type Obs = Pick<GameState, 'boss' | 'player' | 'phase' | 'finisher' | 'deathBeat' | 'time'>

let actSaid = new Set<string>()
let stunSaid = 0
let wasStunned = false
let queue: string[] = []
let current: { text: string; at: number; instant?: boolean } | null = null

/** updateGame の後に毎コマ呼ぶ (DEV ?hud=v2 の時だけ)。言うきっかけを拾って順に出す。 */
export function observeHudV2(s: Obs): void {
  if (current && (s.time < current.at || s.phase !== 'playing')) { current = null; queue = [] }
  if (s.phase !== 'playing') { wasStunned = false; return }
  if (s.boss.executesTaken >= 1 && !actSaid.has(s.boss.id)) {
    actSaid.add(s.boss.id)
    queue.push(HUD_V2_LINES.actMarks, HUD_V2_LINES.actRefill)
  }
  const stunned = s.player.state === 'stunned'
  if (stunned && !wasStunned && stunSaid < HUD_V2_STUN_SAY_TIMES) {
    stunSaid += 1
    // 崩れはすぐ致命が来る (覚醒獣は約0.3秒で紫) = 待たせずに割り込み、一字ずつでなく一度に出す
    current = { text: HUD_V2_LINES.stunParry, at: s.time, instant: true }
  }
  wasStunned = stunned
  if (current && s.time - current.at >= HUD_V2_LINE_SEC) current = null
  // 致命の演出・負けの間は新しく言い始めない
  if (!current && queue.length > 0 && !s.finisher && !s.deathBeat) current = { text: queue.shift()!, at: s.time }
}

/** 今出しているひと言と、出始めてからの秒数。 */
export function hudV2Line(time: number): { text: string; age: number; instant: boolean } | null {
  if (!current) return null
  const age = time - current.at
  return age >= 0 && age < HUD_V2_LINE_SEC ? { text: current.text, age, instant: !!current.instant } : null
}

export function resetHudV2ForTest(): void {
  look = 'answered'; actSaid = new Set(); stunSaid = 0; wasStunned = false; queue = []; current = null
}
